import React, { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useProductBrain } from '../../contexts/ProductBrainContext';
import { services } from '../../services/productBrain';
import { extractDiscovery } from '../../services/ollama';
import EvidenceAssessmentFields from '../evidence/EvidenceAssessmentFields';
import ModelPicker from './ModelPicker';

const EVIDENCE_TYPES = { interview:'Customer interview', support_ticket:'Support conversation', survey:'Survey response', usability:'Usability test', link:'Other' };
const NEW = 'new';

// Turns a transcript or notes into reviewed evidence. IDs are fixed when the
// signals are extracted, so a retried confirmation never duplicates records.
export default function DiscoveryExtract({ item, onDone, model: sharedModel }) {
  const { data } = useProductBrain();
  const [ownModel, setModel] = useState('');
  const model = sharedModel ?? ownModel;
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [evidenceType, setEvidenceType] = useState('interview');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const abort = useRef(null);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => { setResult(null); setRows([]); setError(''); }, [item.id]);

  const extract = async () => {
    setBusy(true); setError(''); setResult(null); abort.current = new AbortController();
    try {
      const { answer } = await extractDiscovery({ model, text: item.content, data, signal: abort.current.signal });
      setResult(answer);
      setRows(answer.signals.map(s => ({ ...s, include: true, relationship: 'unreviewed', source_identity: `inbox:${item.id}`, target: s.problem_id || NEW, evidenceId: crypto.randomUUID(), problemId: crypto.randomUUID() })));
    } catch (e) { setError(e.name === 'AbortError' ? 'Request cancelled.' : e.message); }
    finally { setBusy(false); }
  };
  const update = (index, patch) => setRows(current => current.map((r, i) => i === index ? { ...r, ...patch } : r));
  const confirm = async () => {
    setBusy(true); setError('');
    try {
      for (const row of rows.filter(r => r.include)) {
        let problemId = row.target;
        if (row.target === NEW) {
          problemId = row.problemId;
          if (!await services.problems.getById(problemId)) await services.problems.create({ id: problemId, title: row.statement, description: row.quote ? `“${row.quote}”` : row.statement, status: 'discovered', severity: 'medium', target_segment: result.segment, confidence_score: { desirability: 0, feasibility: 0, viability: 0 }, time_horizon: 'Next' });
        }
        if (!await services.evidence.getById(row.evidenceId)) await services.evidence.create({ id: row.evidenceId, problem_card_id: problemId, title: row.statement, description: row.quote ? `“${row.quote}”\n\n${result.summary}` : result.summary, type: evidenceType, relationship: row.relationship, source_identity: row.source_identity, source_url: item.source_url || '', observed_at: item.created_at, strength: 3 });
      }
      await services.inbox.update(item.id, { status: 'processed', destination: 'discovery', destination_id: null });
      onDone();
    } catch (e) { setError(e.message || 'Could not save the selected evidence.'); }
    finally { setBusy(false); }
  };
  const chosen = rows.filter(r => r.include);
  return <section aria-label="Research extraction">
    <h3>Extract evidence from research</h3>
    <p className="v2-muted">For transcripts, support threads, and meeting notes. SPCC summarizes and proposes problem signals; you choose what to save.</p>
    {sharedModel === undefined && <ModelPicker value={model} onChange={setModel}/>}
    <div className="v2-actions"><button type="button" className="v2-button secondary" disabled={!model || busy} onClick={extract}><Sparkles size={15}/>{busy && !result ? 'Reading…' : 'Extract with AI'}</button>{busy && !result && <button type="button" className="v2-button secondary" onClick={() => abort.current?.abort()}>Cancel</button>}</div>
    {error && <p role="alert" className="v2-error">{error}</p>}
    {result && <div className="v2-coach">
      <span className="v2-label">AI interpretation · review required</span>
      <p>{result.summary}</p>
      <dl className="v2-details"><dt>Segment</dt><dd>{result.segment || 'Not identified'}</dd><dt>Sentiment</dt><dd>{result.sentiment}</dd><dt>Themes</dt><dd>{result.themes.join(', ') || '—'}</dd></dl>
      {!rows.length && <p>No distinct problem signals were found.</p>}
      {rows.map((row, index) => <div className="v2-signal-row" key={row.evidenceId}>
        <label><input type="checkbox" checked={row.include} onChange={e => update(index, { include: e.target.checked })}/> <strong>{row.statement}</strong></label>
        <EvidenceAssessmentFields value={row} onChange={patch => update(index, patch)} prefix={`Signal ${index + 1}`}/>
        {row.quote && <blockquote>“{row.quote}”</blockquote>}
        <label className="v2-field"><span>Add as evidence to</span><select value={row.target} disabled={!row.include} onChange={e => update(index, { target: e.target.value })}><option value={NEW}>A new problem: “{row.statement.slice(0, 60)}”</option>{data.problems.map(p => <option key={p.id} value={p.id}>{p.title}{p.id === row.problem_id ? ' (suggested match)' : ''}</option>)}</select></label>
      </div>)}
      {rows.length > 0 && <>
        <label className="v2-field"><span>Evidence type</span><select value={evidenceType} onChange={e => setEvidenceType(e.target.value)}>{Object.entries(EVIDENCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <button type="button" className="v2-button" disabled={busy || !chosen.length} onClick={confirm}>{busy ? 'Saving…' : `Save ${chosen.length} signal${chosen.length === 1 ? '' : 's'} as evidence`}</button>
      </>}
    </div>}
  </section>;
}
